import React, { useMemo, useState } from 'react';
import { IconProvider } from '@/components/ui';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { DialogNavigationSelector } from '../DialogNavigationSelector';
import { DialogField, getCachedDialogs } from '@/lib/stores/dialogStore';

interface ConnectionBadgeProps {
  field: DialogField;
  sectionIndex: number;
  sectionIdentifiers: string[];
  sections?: { section_identifier: string; name: string }[];
  currentSectionFields?: DialogField[];
  currentDialogId?: string;
  effectiveGameId?: string;
  onUpdateField: (sectionIndex: number, fieldId: string, updates: Partial<DialogField>) => void;
  showFieldOption?: boolean;
}

export const ConnectionBadge: React.FC<ConnectionBadgeProps> = ({
  field,
  sectionIndex,
  sectionIdentifiers,
  sections,
  currentSectionFields,
  currentDialogId,
  effectiveGameId,
  onUpdateField,
  showFieldOption = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Get available dialogs for connection description
  const availableDialogs = useMemo(() => {
    if (!effectiveGameId) return [];
    return getCachedDialogs(effectiveGameId).filter(d => d.id !== currentDialogId);
  }, [effectiveGameId, currentDialogId]);

  // Get current section identifier
  const currentSectionIdentifier = useMemo(() => {
    if (!sections || sectionIndex >= sections.length) return undefined;
    return sections[sectionIndex]?.section_identifier;
  }, [sections, sectionIndex]);

  // Check if field has a connection (including empty strings which indicate transitioning state)
  const hasConnection = 
    field.next_field !== null && field.next_field !== undefined ||
    field.next_section !== null && field.next_section !== undefined ||
    field.next_dialog !== null && field.next_dialog !== undefined ||
    (field as any).next_node !== null && (field as any).next_node !== undefined;

  // Generate connection description
  const getConnectionDescription = useMemo(() => {
    if (field.next_dialog) {
      const targetDialog = availableDialogs.find(d => d.id === field.next_dialog);
      if (targetDialog) {
        if (field.next_dialog_section || (field as any).next_dialog_node) {
          return `${targetDialog.name} • ${field.next_dialog_section || (field as any).next_dialog_node}`;
        }
        return targetDialog.name;
      }
      return 'External dialog';
    }
    // Check both next_node (new) and next_section (legacy)
    const targetNodeOrSection = (field as any).next_node || field.next_section;
    if (targetNodeOrSection) {
      const targetSection = sections?.find(s => s.section_identifier === targetNodeOrSection);
      return targetSection?.name || targetNodeOrSection;
    }
    if (field.next_field) {
      const targetField = currentSectionFields?.find(f => f.id === field.next_field);
      if (targetField) {
        const fieldLabel = targetField.text?.substring(0, 25) || `Field ${targetField.order_index + 1}`;
        return `${targetField.type}: ${fieldLabel}${targetField.text && targetField.text.length > 25 ? '...' : ''}`;
      }
      return 'Field in section';
    }
    return null;
  }, [field, availableDialogs, sections, currentSectionFields]);

  const handleClearConnection = () => {
    onUpdateField(sectionIndex, field.id, {
      next_field: null,
      next_section: null,
      next_dialog: null,
      next_dialog_section: null,
      next_node: null,
      next_dialog_node: null,
    } as any);
    setIsOpen(false);
  };

  const handleGoTo = () => {
    // TODO: Implement navigation to the connected item
    console.log('Go to:', field.next_field || field.next_section || field.next_dialog);
  };

  if (!hasConnection) {
    // Show simple button to add connection
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed"
            type="button"
          >
            <IconProvider iconstring="fa-plus" size={10} className="mr-1" />
            Add connection
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-3" align="start" side="bottom">
          <DialogNavigationSelector
            navigation={{
              next_field: field.next_field,
              next_section: field.next_section,
              next_dialog: field.next_dialog,
              next_dialog_section: field.next_dialog_section,
              next_node: (field as any).next_node, // Include for future compatibility
              next_dialog_node: (field as any).next_dialog_node,
            }}
            onNavigationChange={(updates) => onUpdateField(sectionIndex, field.id, updates)}
            sectionIdentifiers={sectionIdentifiers}
            sections={sections}
            currentSectionFields={currentSectionFields}
            currentItemId={field.id}
            currentSectionIdentifier={currentSectionIdentifier}
            currentDialogId={currentDialogId}
            gameId={effectiveGameId}
            showFieldOption={showFieldOption}
            label="Connect to:"
            size="sm"
          />
        </PopoverContent>
      </Popover>
    );
  }

  // Show connection badge with popover menu
  return (
    <div className="flex items-center gap-1">
      <IconProvider 
        iconstring="fa-arrow-right" 
        size={10} 
        className="text-green-600" 
      />
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-green-700 hover:text-green-800 hover:bg-green-50 font-medium"
            type="button"
          >
            {getConnectionDescription || 'Select...'}
            <IconProvider iconstring="fa-chevron-down" size={8} className="ml-1 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-3" align="start" side="bottom">
          <DialogNavigationSelector
            navigation={{
              next_field: field.next_field,
              next_section: field.next_section,
              next_dialog: field.next_dialog,
              next_dialog_section: field.next_dialog_section,
              next_node: (field as any).next_node, // Include for future compatibility
              next_dialog_node: (field as any).next_dialog_node,
            }}
            onNavigationChange={(updates) => onUpdateField(sectionIndex, field.id, updates)}
            sectionIdentifiers={sectionIdentifiers}
            sections={sections}
            currentSectionFields={currentSectionFields}
            currentItemId={field.id}
            currentSectionIdentifier={currentSectionIdentifier}
            currentDialogId={currentDialogId}
            gameId={effectiveGameId}
            showFieldOption={showFieldOption}
            label="Connect to:"
            size="sm"
          />
          
          {/* Action Buttons */}
          <div className="flex items-center gap-2 mt-3 pt-3 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={handleGoTo}
              className="flex-1 text-xs"
              type="button"
            >
              <IconProvider iconstring="fa-arrow-up-right-from-square" size={10} className="mr-1" />
              Go to
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearConnection}
              className="flex-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
              type="button"
            >
              <IconProvider iconstring="fa-xmark" size={10} className="mr-1" />
              Clear
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
